import { Box, IconButton, Stack } from "@mui/material";
import { ReactNode, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useProtectedAxiosInstance } from "../../../hooks/axiosInstance";
import CustomTable from "../../common/tables/Table";
import { useNotificationContext } from "../../../context/notificationContext";
import errorResolver from "../../../utils/error-resolver";
import { routerPaths } from "../../../constants/Paths";

import { GatewayListRequest } from "../../../services/Endpoints/gateway";
import { Receipt1 } from "iconsax-react";
import { useUrl } from "../../../hooks/urlEncoding";
import {
  PaymentAdListColumnFilterable,
  PaymentAdListColumnSortable,
} from "../../../services/Endpoints/payments/PaymentAdListEndpoint";
import {
  FilterOperatorEnum,
  SortDirectionEnum,
} from "../../../services/Schemas/ListQueryString";
import LinkWithPopover from "../common/LinkWithPopover";
import { GatewaySchema } from "../../../services/Schemas/Gateway";
import EditAction from "../../common/edit-action/EditAction";
import { AccessEnums } from "../../../constants/accessEnum";
import { useCanAccess } from "../../../hooks/canAccess";
import NumberFilter from "../../common/tables/filters/NumberFilter";
import TextFilter from "../../common/tables/filters/TextFilter";
import { ITableSchema } from "../../../utils/types";
import useTableSchema from "../../../hooks/useTableSchema";
import useUrlSort from "../../../hooks/useUrlSort";

interface IPayment {
  caption: string;
  id: number;
  image_url: string | ReactNode;
  operations: string | ReactNode;
}

export interface ISupportTabsComponent {
  walletReload: boolean;
  setWalletReload: (payload: boolean) => void;
  setServerInfo?: (serverInfo: GatewaySchema) => void;
  setOpenModal: (payload: boolean) => void;
}

export default function PaymentTableComponent({
  walletReload,
  setWalletReload,
  setServerInfo,
  setOpenModal,
}: Readonly<ISupportTabsComponent>) {
  const { queryStringUrl, parseQueryString, setParamWithSearchParams } =
    useUrl();
  const parsed = parseQueryString();
  const initialPage = parsed.page ? Number(parsed.page) : 1;
  const initialRows = parsed.count ? Number(parsed.count) : 10;
  const [value, setValue] = useState("1");
  const [page, setPage] = useState(initialPage);
  const [rowsPerPage, setRowsPerPage] = useState(initialRows);

  const [dataPayment, setDataPayment] = useState<IPayment[] | []>([]);
  const [total, setTotal] = useState(0);
  const navigate = useNavigate();
  const [waiting, setWaiting] = useState(false);
  const protectedInstance = useProtectedAxiosInstance();
  const { setNotification } = useNotificationContext();
  const { can } = useCanAccess();
  const location = useLocation();
  const { parseUrlSorts, applyUrlSorts } = useUrlSort();
  const sorts = parseUrlSorts();
  const handleEdit = (serverInfo: GatewaySchema) => {
    setServerInfo && setServerInfo(serverInfo);
    setOpenModal(true);
  };

  const getDataPayment = async () => {
    try {
      setWaiting(true);
      const paymentListRequest = new GatewayListRequest(protectedInstance);
      const resp = await paymentListRequest.send({
        page,
        count: rowsPerPage,
        ...parseQueryString(),
      });
      setTotal(resp.data.total);
      const transactions = resp.data.payment_gateways.map(
        ({
          caption,
          id,
          image_url,
          api_url,
          driver,
          gateway_merchant_id,
          status,
          url_secret_key,
          secret,
        }) => ({
          caption,
          id,
          image_url: (
            <Stack
              width="80px"
              height="40px"
              overflow="hidden"
              justifyContent="center"
              mx="auto"
            >
              <img
                src={image_url ?? ""}
                style={{ height: "40px", width: "80px", objectFit: "contain" }}
              />
            </Stack>
          ),
          operations: (
            <Stack
              gap="3px"
              justifyContent={"center"}
              alignContent="center"
              direction="row"
            >
              <EditAction
                disabled={!can(AccessEnums.AdminPaymentGatewayCreate)}
                handleEdit={() =>
                  handleEdit({
                    caption,
                    id,
                    image_url,
                    api_url,
                    driver,
                    gateway_merchant_id,
                    status,
                    url_secret_key,
                    secret,
                  })
                }
              />
              <LinkWithPopover
                disabled={!can(AccessEnums.AdminPaymentReceiptsList)}
                title="رسیدها"
              >
                <IconButton>
                  <Stack
                    alignItems="center"
                    justifyContent="center"
                    component={Link}
                    to={queryStringUrl(routerPaths.RECEIPTS, {
                      filters: {
                        [PaymentAdListColumnFilterable.PAYMENT_GATEWAY_ID]: {
                          column:
                            PaymentAdListColumnFilterable.PAYMENT_GATEWAY_ID,
                          operator: FilterOperatorEnum.EQUAL,
                          value: id,
                        },
                      },
                      sorts: {
                        [PaymentAdListColumnSortable.ID]: {
                          column: PaymentAdListColumnSortable.ID,
                          direction: SortDirectionEnum.DESC,
                        },
                      },
                    })}
                  >
                    <Receipt1 size="20" color={"black"} />
                  </Stack>
                </IconButton>
              </LinkWithPopover>
            </Stack>
          ),
        })
      );
      setDataPayment(transactions);
      setWaiting(false);
    } catch (error) {
      errorResolver({ error, setNotification });
      setWaiting(false);
      console.error(error);
    }
  };

  useEffect(() => {
    getDataPayment();
  }, [walletReload, page, rowsPerPage, value, location.search]);

  const paymentColumns: ITableSchema[] = [
    {
      key: "id",
      label: "شناسه",
      minWidth: 170,
      align: "center",
      FilterComponent: NumberFilter,
      sortable: true,
    },
    {
      key: "caption",
      label: "نام",
      minWidth: 100,
      align: "center",
      FilterComponent: TextFilter,
    },
    { key: "image_url", label: "تصویر", minWidth: 170, align: "center" },
    {
      key: "operations",
      label: "عملیات",
      minWidth: 170,
      align: "center",
    },
  ];
  const { filtersSchema, columns } = useTableSchema(paymentColumns);

  const handleChangePage = (newPage: number) => {
    setPage(newPage);
    setParamWithSearchParams("page", String(newPage));
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newRows = +event.target.value;
    setRowsPerPage(newRows);
    setParamWithSearchParams("count", String(newRows));
  };

  return (
    <Box sx={{ width: "100%", typography: "body1", backgroundColor: "white" }}>
      <CustomTable
        reloadAction={() => setWalletReload(!walletReload)}
        hasWaiting={waiting}
        hasSearchFilter
        hasBottomPagination
        hasTopPagination
        columns={columns}
        count={total}
        handleChangePage={handleChangePage}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
        page={page}
        rows={dataPayment}
        rowsPerPage={rowsPerPage}
        filtersSchema={filtersSchema}
        onApplySorts={applyUrlSorts}
        sorts={sorts}
      />
    </Box>
  );
}
